import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';
import { getServiceWorkerRegistration } from '../serviceWorkerRegistration';

/**
 * Konverterar en base64url-kodad VAPID-nyckel till Uint8Array.
 *
 * `PushManager.subscribe()` kräver rå bytes; servern skickar base64url (utan
 * padding, med `-`/`_` i stället för `+`/`/`).
 *
 * @param {string} base64 - VAPID-nyckeln i base64url.
 * @returns {Uint8Array} Nyckeln som bytes.
 */
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * True när webbläsaren kan ta emot web push alls.
 *
 * @returns {boolean}
 */
export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * True när sidan körs som installerad app (hemskärm / standalone).
 *
 * iOS Safari tillåter push ENDAST i det läget, så UI:t måste kunna säga
 * "lägg till på hemskärmen först" i stället för att visa en knapp som inte
 * kan fungera.
 *
 * @returns {boolean}
 */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

/**
 * True för iOS/iPadOS, där push kräver installerad app.
 *
 * @returns {boolean}
 */
export function isIos() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  const iPadOs =
    window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOs;
}

/**
 * Hanterar spelarens push-prenumeration och notisinställningar.
 *
 * Behörighetsfrågan ställs ALDRIG vid sidladdning — bara från `subscribe()`,
 * som anropas av en knapp. En prompt utan användarhandling blockeras av
 * webbläsaren permanent, och då går den inte att fråga igen.
 *
 * @returns {{
 *   supported: boolean, configured: boolean, permission: string,
 *   subscribed: boolean, prefs: Object|null, devices: number,
 *   loading: boolean, error: string,
 *   needsInstall: boolean,
 *   subscribe: () => Promise<boolean>,
 *   unsubscribe: () => Promise<boolean>,
 *   savePrefs: (patch: Object) => Promise<boolean>
 * }}
 */
export default function usePushNotifications() {
  const { getAuthHeaders, user } = useAuth();
  const supported = pushSupported();

  const [configured, setConfigured] = useState(false);
  const [publicKey, setPublicKey] = useState('');
  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'denied'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [devices, setDevices] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // iOS kräver installerad app för push; övriga plattformar gör inte det.
  const needsInstall = supported && isIos() && !isStandalone();

  useEffect(() => {
    let cancelled = false;

    async function init() {
      if (!supported) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/notifications/push/vapid-key`);
        const data = await res.json();
        if (cancelled) return;
        setConfigured(Boolean(data.configured));
        setPublicKey(data.public_key || '');

        const registration = await getServiceWorkerRegistration();
        if (registration) {
          const existing = await registration.pushManager.getSubscription();
          if (!cancelled) setSubscribed(Boolean(existing));
        }

        if (user) {
          const prefRes = await fetch(
            `${API_URL}/api/notifications/push/preferences`,
            { headers: getAuthHeaders() }
          );
          if (prefRes.ok && !cancelled) {
            const prefData = await prefRes.json();
            setPrefs(prefData);
            setDevices(prefData.devices || 0);
          }
        }
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [supported, user, getAuthHeaders]);

  const subscribe = useCallback(async () => {
    if (!supported || !configured || !publicKey) return false;
    setError('');
    try {
      // Behörigheten frågas här, inuti klick-handlern.
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') return false;

      const registration = await getServiceWorkerRegistration();
      if (!registration) {
        setError('Service worker saknas');
        return false;
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const res = await fetch(`${API_URL}/api/notifications/push/subscribe`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription.toJSON()),
      });
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return false;
      }
      setSubscribed(true);
      setDevices((n) => n + 1);
      return true;
    } catch (e) {
      setError(String(e.message || e));
      return false;
    }
  }, [supported, configured, publicKey, getAuthHeaders]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return false;
    setError('');
    try {
      const registration = await getServiceWorkerRegistration();
      const subscription = registration
        ? await registration.pushManager.getSubscription()
        : null;

      // Berätta för servern FÖRE vi river prenumerationen lokalt — annars
      // finns ingen endpoint kvar att peka ut, och raden blir kvar och
      // failar tills failure_count rensar den.
      await fetch(`${API_URL}/api/notifications/push/unsubscribe`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(subscription ? { endpoint: subscription.endpoint } : {}),
      });
      if (subscription) await subscription.unsubscribe();

      setSubscribed(false);
      setDevices((n) => Math.max(0, n - 1));
      return true;
    } catch (e) {
      setError(String(e.message || e));
      return false;
    }
  }, [supported, getAuthHeaders]);

  const savePrefs = useCallback(
    async (patch) => {
      setError('');
      try {
        const res = await fetch(
          `${API_URL}/api/notifications/push/preferences`,
          {
            method: 'PATCH',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(patch),
          }
        );
        if (!res.ok) {
          setError(`HTTP ${res.status}`);
          return false;
        }
        // Läs om från servern i stället för att gissa nytt läge lokalt — en
        // gissning som slinter gör att reglaget ljuger om vad som gäller.
        const data = await res.json();
        setPrefs(data);
        setDevices(data.devices || 0);
        return true;
      } catch (e) {
        setError(String(e.message || e));
        return false;
      }
    },
    [getAuthHeaders]
  );

  return {
    supported,
    configured,
    permission,
    subscribed,
    prefs,
    devices,
    loading,
    error,
    needsInstall,
    subscribe,
    unsubscribe,
    savePrefs,
  };
}
